import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Check, LayoutGrid } from 'lucide-react';
import { api } from '../../lib/api';

const LAST_PROGRAM_KEY = 'map_last_loyalty_program';

interface Program {
  id: string;
  name: string;
  programType?: string;
  memberCount?: number;
}

export function ProgramSwitcher() {
  const { id: activeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: programs = [] } = useQuery<Program[]>({
    queryKey: ['loyalty-programs-switcher'],
    queryFn: () =>
      api.get('/api/v1/loyalty/programs').then((r) => r.data.programs ?? r.data),
    staleTime: 60_000,
  });

  const activeProgram = programs.find((p) => p.id === activeId);

  // Persist last-visited program
  useEffect(() => {
    if (activeId) localStorage.setItem(LAST_PROGRAM_KEY, activeId);
  }, [activeId]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function selectProgram(id: string) {
    setOpen(false);
    navigate(`/loyalty/${id}/overview`);
  }

  return (
    <div className="px-2 mb-2 relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Switch loyalty program"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition-colors text-left"
      >
        <div className="min-w-0">
          <div className="text-xs font-semibold text-zinc-800 truncate">
            {activeProgram?.name ?? 'Select program'}
          </div>
          {activeProgram && (
            <div className="text-[10px] text-zinc-400 truncate">
              {activeProgram.programType ?? ''}
              {activeProgram.memberCount != null
                ? ` · ${activeProgram.memberCount} members`
                : ''}
            </div>
          )}
        </div>
        <ChevronDown size={13} className="text-zinc-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute left-2 right-2 top-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {programs.map((p) => (
            <button
              key={p.id}
              onClick={() => selectProgram(p.id)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 transition-colors text-left border-b border-zinc-100 last:border-b-0"
            >
              <Check
                size={12}
                className={p.id === activeId ? 'text-violet-600' : 'text-transparent'}
              />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-zinc-800 truncate">{p.name}</div>
                {p.memberCount != null && (
                  <div className="text-[10px] text-zinc-400">{p.memberCount} members</div>
                )}
              </div>
            </button>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              navigate('/loyalty', { state: { manage: true } });
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-violet-600 hover:bg-zinc-50 transition-colors border-t border-zinc-100"
          >
            <LayoutGrid size={12} /> Manage programs…
          </button>
        </div>
      )}
    </div>
  );
}

// Helper used by LoyaltyProgramsPage to auto-redirect to last visited program
export function getLastLoyaltyProgramId(): string | null {
  return localStorage.getItem(LAST_PROGRAM_KEY);
}
